import { Snackbar } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useRef, useState } from 'react';

import { ReactComponent as AttachmentIcon } from '../../../../assets/icons/attachment.svg';
import { ReactComponent as CloseIcon } from '../../../../assets/icons/close.svg';
import useFileUploadWithValidation, { UploadedFile } from '../../../../hooks/useFileUploadWithValidation';
import { WorkSelectors } from '../../../../reducers/work';
import { generateUniqueId } from '../../../../services/utils';
import { attachmentsStorage, StoredAttachment } from '../../../../services/workStorage';
import { colors } from '../../../../theme';
import { getFileIcon } from '../../../../utils/fileTypeHelpers';
import AttachmentItem from '../../components/AttachmentItem';
import S from './styled';

interface Props {
    attachmentIds: number[];
    setAttachmentIds: React.Dispatch<React.SetStateAction<number[]>>;
    title?: string;
    instructions?: string;
    resetKey?: number;
    onUploadingChange?: (isUploading: boolean) => void;
    /** If true, persists files to sessionStorage. Default: true */
    persistToStorage?: boolean;
    /** If true, enables clipboard paste support. Default: true */
    enablePaste?: boolean;
    /** If true, only shows completed files in attachment list. Default: false */
    showOnlyCompleted?: boolean;
    /** If true, hides the title and instructions header. Default: false */
    hideHeader?: boolean;
    /** If true, uploaded file is a bug report. Default: false */
    isBug?: boolean;
}

const AttachmentUpload: React.FC<Props> = ({ 
    attachmentIds: _attachmentIds,
    setAttachmentIds,
    title,
    instructions,
    resetKey = 0,
    onUploadingChange,
    persistToStorage = true,
    enablePaste = true,
    showOnlyCompleted = false,
    hideHeader = false,
    isBug = false
}) => {
    const [ files, setFiles ] = useState<UploadedFile[]>([]);
    const [ fileError, setFileError ] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { work } = WorkSelectors();

    const handleFilesSelect = useFileUploadWithValidation({
        workId: work?.id,
        isBug,
        onFilesUpdate: setFiles,
        onError: setFileError,
        onFileComplete: (fileId) => {
            setAttachmentIds((prevIds) => [ ...prevIds, fileId ]);
        },
        onFileRemove: (fileId) => {
            setAttachmentIds((prevIds) =>
                prevIds.filter((prevId) => prevId !== fileId)
            );
        }
    });

    useEffect(() => {
        if (resetKey !== undefined && work?.id) {
            setFiles([]);
            setAttachmentIds([]);
            attachmentsStorage.clear(work.id);
        }
    }, [ resetKey, setAttachmentIds, work?.id ]);

    useEffect(() => {
        if (!persistToStorage || !work?.id) {
            return;
        }

        const storedFiles = attachmentsStorage.load(work.id);
        if (storedFiles) {
            const restoredFiles: UploadedFile[] = storedFiles.map(stored => ({
                id: stored.id,
                fileId: stored.fileId,
                file: new File([], stored.fileName, { type: stored.fileType }),
                status: stored.status === 'uploading' ? 'completed' : stored.status,
                uploadProgress: stored.status === 'uploading' ? 100 : stored.uploadProgress
            }));
            
            setFiles(restoredFiles);
            
            const restoredIds = storedFiles
                .filter(f => f.fileId !== undefined)
                .map(f => f.fileId as number);
            if (restoredIds.length > 0) {
                setAttachmentIds(restoredIds);
            }
        }
    }, [ persistToStorage, work?.id ]);

    useEffect(() => {
        if (!persistToStorage || !work?.id) {
            return;
        }
        
        const filesToStore: StoredAttachment[] = files.map(file => ({
            id: file.id,
            fileId: file.fileId,
            fileName: file.file.name,
            fileType: file.file.type,
            fileSize: file.file.size,
            status: file.status,
            uploadProgress: file.uploadProgress
        }));
        
        attachmentsStorage.save(work.id, filesToStore);
    }, [ files, persistToStorage, work?.id ]);

    useEffect(() => {
        const isUploading = files.some(file => file.status === 'uploading');
        onUploadingChange?.(isUploading);
    }, [ files, onUploadingChange ]);

    const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const selectedFiles = Array.from(e.target.files).filter(
                (file) =>
                    file.type.startsWith('image/') ||
                    file.type.startsWith('video/')
            );

            if (selectedFiles.length > 0) {
                const selectedFilesWithIds = selectedFiles.map((file) => ({
                    file,
                    id: generateUniqueId()
                }));
                handleFilesSelect(selectedFilesWithIds);
            }
        }
        // Reset the input so the same file can be selected again
        if (e.target) {
            e.target.value = '';
        }
    }, [ handleFilesSelect ]);

    const handleAttachmentClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const removeAttachment = useCallback((idToRemove: number): void => {
        const fileToRemove = files.find(
            (file) => file.fileId === idToRemove
        );

        if (fileToRemove && fileToRemove.fileId) {
            setAttachmentIds((prevIds) =>
                prevIds.filter(
                    (prevId) => prevId !== Number(fileToRemove.fileId)
                )
            );
        }

        setFiles((prevFiles) =>
            prevFiles.filter((file) => file.fileId !== idToRemove)
        );
    }, [ files, setAttachmentIds ]);

    const cancelUpload = useCallback((fileId: number): void => {
        const fileToCancel = files.find((f) => f.id === fileId);

        if (fileToCancel && fileToCancel.uploadController) {
            fileToCancel.uploadController.abort();
        }

        removeAttachment(fileId);
    }, [ files, removeAttachment ]);

    useEffect(() => {
        if (!enablePaste) {
            return;
        }
        
        const handlePaste = async (event: ClipboardEvent): Promise<void> => {
            const clipboardItems = event.clipboardData?.items;
            if (!clipboardItems) {
                return;
            }

            const mediaFiles: File[] = [];

            for (let i = 0; i < clipboardItems.length; i++) {
                const item = clipboardItems[i];
                if (
                    item.type.startsWith('image/') ||
                    item.type.startsWith('video/')
                ) {
                    const file = item.getAsFile();
                    if (file) {
                        const MAX_FILE_SIZE = 20 * 1024 * 1024;
                        if (file.size <= MAX_FILE_SIZE) {
                            mediaFiles.push(file);
                        } else {
                            alert(
                                'File from clipboard was not added because it exceeds the 20MB size limit.'
                            );
                        }
                    }
                }
            }

            if (mediaFiles.length > 0) {
                event.preventDefault();
                const filesWithIds = mediaFiles.map((file) => ({
                    file,
                    id: generateUniqueId()
                }));
                await handleFilesSelect(filesWithIds);
            }
        };

        document.addEventListener('paste', handlePaste);

        return () => {
            document.removeEventListener('paste', handlePaste);
        };
    }, [ enablePaste, handleFilesSelect ]);

    const defaultTitle = 'Attachments';
    const defaultInstructions = 'Please upload a video (or, if that\'s not possible, at least an image) that demonstrates what you built. If your work is intended to run on multiple devices, make sure to show how it works on each of them.';

    return (
        <S.Root>
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                multiple
                onChange={handleFileChange}
                style={{ display: 'none' }}
            />
            {!hideHeader && (
                <>
                    <Typography sx={S.labelTitle} component="label">
                        {title || defaultTitle}
                        <span
                            style={{
                                color: colors.yellow,
                                marginLeft: '5px',
                                position: 'relative',
                                display: 'inline-block'
                            }}
                        >
                                !
                        </span>
                    </Typography>
                    <Typography
                        sx={S.labelInstructions}
                        component="label"
                    >
                        {instructions || defaultInstructions}
                    </Typography>
                </>
            )}
            <div
                style={{
                    padding: 12
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '8px',
                        maxHeight: '150px',
                        overflowY: 'auto'
                    }}
                >
                    <div
                        onClick={handleAttachmentClick}
                        style={{
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            padding: '4px',
                            marginRight: '4px'
                        }}
                    >
                        <AttachmentIcon color={colors.yellow} />
                    </div>
                    {(showOnlyCompleted 
                        ? files.filter(f => f.status === 'completed') 
                        : files
                    ).map((attachedFile) => (
                        <AttachmentItem
                            key={`attachment-${attachedFile.id}`}
                            file={{
                                id:
                                    Number(attachedFile.fileId) ||
                                    generateUniqueId(),
                                name: attachedFile.file.name,
                                type: attachedFile.file.type
                            }}
                            index={Number(attachedFile.fileId)}
                            removeAttachment={removeAttachment}
                        />
                    ))}
                </div>
            </div>

            {files.filter(
                (file) => file.status === 'uploading'
            ).length > 0 && (
                <div
                    style={{
                        marginTop: 16,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px'
                    }}
                >
                    {files
                        .filter(
                            (file) => file.status === 'uploading'
                        )
                        .map((file) => (
                            <div
                                key={`progress-${file.id}`}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                    gap: '8px'
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        width: '160px'
                                    }}
                                >
                                    {getFileIcon(file.file.type)}
                                    <span
                                        style={{
                                            marginLeft: '8px',
                                            overflow: 'hidden',
                                            textOverflow:
                                                'ellipsis',
                                            whiteSpace: 'nowrap',
                                            flexGrow: 1,
                                            color: colors.white
                                        }}
                                    >
                                        {file.file.name}
                                    </span>
                                </div>

                                <div
                                    style={{
                                        width: '100%',
                                        flexGrow: 1,
                                        position: 'relative'
                                    }}
                                >
                                    <div
                                        style={{
                                            width: '100%',
                                            height: '4px',
                                            backgroundColor:
                                                '#20222D',
                                            borderRadius: '2px'
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: `${
                                                    file.uploadProgress ||
                                                    0
                                                }%`,
                                                height: '100%',
                                                backgroundColor:
                                                    colors.yellow,
                                                borderRadius: '2px',
                                                transition:
                                                    'width 0.3s ease'
                                            }}
                                        />
                                    </div>
                                </div>

                                <span
                                    style={{
                                        minWidth: '40px',
                                        textAlign: 'right',
                                        color: colors.yellow
                                    }}
                                >
                                    {file.uploadProgress}%
                                </span>

                                <div
                                    style={{
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '2px'
                                    }}
                                    onClick={() =>
                                        cancelUpload(file.id)
                                    }
                                >
                                    <CloseIcon
                                        width={12}
                                        height={12}
                                    />
                                </div>
                            </div>
                        ))}
                </div>
            )}
            
            <Snackbar
                open={!!fileError}
                autoHideDuration={6000}
                onClose={() => setFileError(null)}
                message={fileError}
            />
        </S.Root>
    );
};

export default AttachmentUpload;
